import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import axios from "axios";
import { trackEvent } from "../lib/metaPixel";
import "./Quiz.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOADING_MSGS = [
  "Reviewing your answers…",
  "Matching training to your goal…",
  "Dialing in your nutrition targets…",
  "Building your 6-week roadmap…",
  "Adding the finishing touches…",
];

const QUESTIONS = [
  {
    key: "goal",
    title: "What's your #1 goal right now?",
    options: [
      { key: "fat_loss", label: "Lose fat" },
      { key: "muscle_tone", label: "Build muscle tone" },
      { key: "hormones", label: "Balance my energy & mood" },
      { key: "strength_age", label: "Feel stronger as I age" },
      { key: "competition", label: "Prep for a competition" },
    ],
  },
  {
    key: "barrier",
    title: "What's held you back from getting there before?",
    options: [
      { key: "motivation", label: "I lose motivation after a few weeks" },
      {
        key: "dont_know",
        label: "I don't know what actually works for my body/age",
      },
      { key: "no_time", label: "No time — work and family come first" },
      {
        key: "hormones_menopause",
        label:
          "Hormones/menopause changed everything and my old approach stopped working",
      },
    ],
  },
  {
    key: "readiness",
    title: "How ready are you to start?",
    options: [
      { key: "ready_now", label: "I'm ready now — just show me the plan" },
      { key: "within_month", label: "Within the next month" },
      { key: "exploring", label: "Just exploring for now" },
    ],
  },
  {
    key: "age_range",
    title: "What's your age range?",
    options: [
      { key: "40s", label: "40–49" },
      { key: "50s", label: "50–59" },
      { key: "60plus", label: "60+" },
    ],
  },
];

const TOTAL_STEPS = QUESTIONS.length + 1; // +1 for the form

const QuizPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [loadStep, setLoadStep] = useState(0);

  useEffect(() => {
    if (!submitting) {
      setLoadStep(0);
      return;
    }
    const id = setInterval(() => {
      setLoadStep((s) => Math.min(s + 1, LOADING_MSGS.length - 1));
    }, 3500);
    return () => clearInterval(id);
  }, [submitting]);

  const isFormStep = step === QUESTIONS.length;
  const progress = ((step + (isFormStep ? 1 : 0)) / TOTAL_STEPS) * 100;

  const handleSelect = (qKey, optKey) => {
    setAnswers((prev) => ({ ...prev, [qKey]: optKey }));
    // brief pause so the selection is visible, then advance
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const goBack = () => {
    setSubmitError("");
    setStep((s) => Math.max(0, s - 1));
  };

  const validateForm = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Please enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = { ...answers, ...form };
      const res = await axios.post(`${API}/quiz/submit`, payload);
      trackEvent("Lead", { content_name: "Quiz Completed" });
      navigate("/thank-you", { state: res.data });
    } catch (err) {
      setSubmitError(
        "Something went wrong creating your plan. Please try again."
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="sffx-quiz">
      {submitting && (
        <div className="q-overlay">
          <div className="q-overlay-card">
            <div className="q-spinner" />
            <h3>Building Your Plan</h3>
            <p className="q-overlay-msg" key={loadStep}>
              {LOADING_MSGS[loadStep]}
            </p>
            <div className="q-overlay-bar">
              <div className="q-overlay-bar-fill" />
            </div>
            <div className="q-overlay-note">
              This usually takes 20–30 seconds — please keep this window open.
            </div>
          </div>
        </div>
      )}
      <div className="q-topbar">
        <button className="q-logo" onClick={() => navigate("/")}>
          Sassy Full Stack Fitness
        </button>
        <button className="q-back" onClick={goBack} disabled={step === 0}>
          <ArrowLeft size={16} /> Back
        </button>
      </div>

      <div className="q-progress-wrap">
        <div className="q-progress-label">
          Step {Math.min(step + 1, TOTAL_STEPS)} of {TOTAL_STEPS}
        </div>
        <div className="q-progress-track">
          <div className="q-progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="q-body">
        <div className="q-inner">
          {!isFormStep ? (
            <div className="q-step" key={QUESTIONS[step].key}>
              <h1 className="q-title">{QUESTIONS[step].title}</h1>
              <div className="q-options">
                {QUESTIONS[step].options.map((opt) => {
                  const selected = answers[QUESTIONS[step].key] === opt.key;
                  return (
                    <button
                      key={opt.key}
                      className={`q-option${selected ? " selected" : ""}`}
                      onClick={() => handleSelect(QUESTIONS[step].key, opt.key)}
                    >
                      <span className="q-bullet">
                        {selected && <Check size={14} strokeWidth={3} />}
                      </span>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <form className="q-step" onSubmit={handleSubmit}>
              <h1 className="q-title">Your custom 6-week plan is ready!</h1>
              <p className="q-form-intro">
                Tell us where to send it and we'll show it to you right now.
              </p>
              <div className="q-field">
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) =>
                    setForm({ ...form, name: e.target.value })
                  }
                />
                {errors.name && <div className="q-error">{errors.name}</div>}
              </div>
              <div className="q-field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  placeholder="you@email.com"
                  value={form.email}
                  onChange={(e) =>
                    setForm({ ...form, email: e.target.value })
                  }
                />
                {errors.email && <div className="q-error">{errors.email}</div>}
              </div>
              <div className="q-field">
                <label htmlFor="phone">
                  Cell Phone <span className="q-optional">(optional)</span>
                </label>
                <input
                  id="phone"
                  data-testid="quiz-phone-input"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={form.phone}
                  onChange={(e) =>
                    setForm({ ...form, phone: e.target.value })
                  }
                />
              </div>
              {submitError && <div className="q-error">{submitError}</div>}
              <button className="q-submit" type="submit" disabled={submitting}>
                {submitting ? "Building your plan..." : "See My Plan →"}
              </button>
              <div className="q-note">
                {submitting ? (
                  <span>
                    Your coach is personalizing your 6-week plan — this takes a
                    few seconds.
                  </span>
                ) : (
                  <>
                    <span className="dot" /> Free &nbsp;·&nbsp; No credit card
                    &nbsp;·&nbsp; Instant results
                  </>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizPage;
